/**
 * runApi.service.ts
 *
 * Mirrors the Angular RunResolver + RunInfoComponent API calls.
 * Provides typed wrappers for every endpoint the run dashboard touches,
 * so React components can call them the same way Angular's resolver/component did.
 */

import { authStore } from '@services/authStore.service';
import { environment } from '@config/environment.config';
import type {
  PlanEntity,
  ExecuteEntity,
  RunEntity,
  RunUIPermissions,
  MaterialSearchResult,
  MachineSearchResult,
  LabCollection,
  PlanPreviewData,
  PlanEdge,
} from '@/types/run-stage.types';

const ILA_API_BASE = environment.getIlaApiBaseUrl();

// ─────────────────────────────────────────────────────────────────────────────
// Low-level fetch helpers
// ─────────────────────────────────────────────────────────────────────────────

async function handleResponse<T>(res: Response, url: string): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    throw new Error(`Unauthorized request to ${url}`);
  }
  if (res.status >= 500) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Server error on ${url}: ${text}`);
  }
  return (await res.json()) as T;
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res, url);
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'GET',
    headers: authStore.getAuthHeader(),
  });
  return handleResponse<T>(res, url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface RunInfoResponse {
  /** The run entity (nested under `entity` in some endpoints, unwrapped here). */
  entity: RunEntity & {
    graph?: { _id?: string; edges?: unknown[]; vertices?: unknown[] };
    executeGraph?: { _id?: string; edges?: unknown[]; vertices?: unknown[] };
    batchExecutionType?: string;
    fullName?: string;
    isArchived?: boolean;
    isArchivalInProgress?: boolean;
    version?: {
      _id?: string;
      name?: string;
      versionNumber?: number;
      experiment?: {
        _id?: string;
        name?: string;
        fileType?: string;
        runProperties?: { onlyAssignedUsers?: boolean };
      };
    };
    assignees?: Array<{ _id?: string; name?: string; email?: string }>;
  };
}

export interface PlanGraph {
  graph?: {
    edges?: unknown[];
    vertices?: unknown[];
  };
}

export interface RunUsers {
  users: Array<{ _id: string; name?: string; email?: string; role?: string }>;
}

export interface RunUIElement {
  label: string;
  [key: string]: unknown;
}

export interface PlanPreview {
  materials?: unknown[];
  steps?: unknown[];
  selectedBatchType?: string;
  [key: string]: unknown;
}

export interface RunApprovers {
  approvers?: unknown[];
  [key: string]: unknown;
}

export interface RunReport {
  _id?: string;
  reportId?: string;
  name?: string;
  status?: string;
  createdAt?: string;
  [key: string]: unknown;
}

export interface ReturnBookedQuantityPayload {
  materialId: string;
  bookingId?: string;
  runId: string;
  nodeId: string;
  cloneNumber: number;
  bookedQty?: { amount: number; units: string };
  usedQty?: { amount: number; units: string };
  releasedQty?: { amount: number; units: string };
  stepId?: string;
  materialBookingRef?: string;
}

export interface UpdateTimingsPayload {
  _id: string;
  uid?: string;
  startTime?: Date | string | null;
  endTime?: Date | string | null;
  pause?: boolean;
  resume?: boolean;
  status?: string;
  entityType?: string;
}

export interface UpdateExecuteStatusPayload {
  runId: string;
  runUid?: string;
  stepId?: string;
  startTime?: Date | string | null;
  endTime?: Date | string | null;
  status?: string;
  pause?: boolean;
  resume?: boolean;
  entityType?: string;
  [key: string]: unknown;
}

// ─────────────────────────────────────────────────────────────────────────────
// Run Resolver equivalents
// Mirror: RunResolver → /api/run/get/run, /api/plan/get, /api/execute/get
//         /api/plan/getGraph, /api/run/get/users
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the full run entity including its embedded graph reference.
 * Mirrors: POST /api/v2/run/get
 */
export async function getRunInfo(runId: string, source?: string): Promise<RunInfoResponse> {
  return post<RunInfoResponse>('/api/v2/run/get', { runId, source });
}

/**
 * Fetch the plan for a run (returns array, normalised to first element).
 * Mirrors: POST /api/v2/plan/get
 */
export async function getRunPlan(
  runId: string,
  entityType?: string,
  source?: string
): Promise<PlanEntity | null> {
  const res = await post<PlanEntity[] | PlanEntity | null>('/api/v2/plan/get', {
    runId,
    entityType,
    source,
  });
  if (Array.isArray(res)) return res[0] ?? null;
  return res;
}

/**
 * Fetch the execute document for a run (normalised to first element).
 * Mirrors: POST /api/v2/execute/get
 *
 * The v2 endpoint returns {success, data: {execute, steps, parameterDiffs}}.
 * We extract the execute document from this wrapper for backward-compat consumers.
 */
export async function getRunExecute(
  runId: string,
  entityType?: string,
  source?: string
): Promise<ExecuteEntity | null> {
  const res = await post<
    | { success: boolean; data?: { execute?: ExecuteEntity } }
    | ExecuteEntity[]
    | ExecuteEntity
    | null
  >('/api/v2/execute/get', { runId, entityType, source });

  // v2 wrapper: {success: true, data: {execute, steps, parameterDiffs}}
  if (res && typeof res === 'object' && !Array.isArray(res) && 'success' in res) {
    const wrapped = res as { success: boolean; data?: { execute?: ExecuteEntity } };
    return wrapped.data?.execute ?? null;
  }

  if (Array.isArray(res)) return res[0] ?? null;
  return res as ExecuteEntity | null;
}

/**
 * Fetch the plan graph (vertices + edges) for a run.
 * Mirrors: POST /api/v2/plan/getGraph
 */
export async function getPlanGraph(runId: string, source?: string): Promise<PlanGraph> {
  return post<PlanGraph>('/api/v2/plan/getGraph', { runId, source });
}

/**
 * Fetch the users assigned to a run.
 * Mirrors: POST /api/run/get/users
 */
export async function getRunUsers(runId: string, source?: string): Promise<RunUsers> {
  return post<RunUsers>('/api/run/get/users', { run: runId, source });
}

// ─────────────────────────────────────────────────────────────────────────────
// Run UI permissions
// Mirrors: POST /api/run/ui?action=runDetails → array of { label } items
// Labels: 'Abort', 'Pause', 'Resume', 'Online_Mode', 'Offline_Mode', 'Offline_MarkAsCompleted'
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch available UI action buttons for the run (Abort, Pause, Resume, etc.).
 * Mirrors: POST /api/run/ui?action=runDetails
 * Returns a typed `RunUIPermissions` object derived from the label array.
 */
export async function getRunUIElements(
  runId: string,
  entityType?: string,
  source?: string
): Promise<RunUIPermissions> {
  const elements = await post<RunUIElement[]>('/api/run/ui?action=runDetails', {
    _id: runId,
    entityType,
    source,
  });

  const empty: RunUIPermissions = {
    canPause: false,
    canResume: false,
    canAbort: false,
    canOnlineMode: false,
    canOfflineMode: false,
  };

  if (!Array.isArray(elements)) return empty;

  return elements.reduce<RunUIPermissions>((acc, el) => {
    switch (el.label) {
      case 'Abort':
        acc.canAbort = true;
        break;
      case 'Pause':
        acc.canPause = true;
        break;
      case 'Resume':
        acc.canResume = true;
        break;
      case 'Online_Mode':
        acc.canOnlineMode = true;
        break;
      case 'Offline_Mode':
        acc.canOfflineMode = true;
        break;
    }
    return acc;
  }, { ...empty });
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan preview + approvals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the enriched plan preview (materials with step info, scheduling etc.).
 * Mirrors: POST /api/plan/previewRunInfo
 */
export async function getPreviewRunInfo(
  runId: string,
  runUid?: string,
  entityType?: string
): Promise<PlanPreview> {
  return post<PlanPreview>('/api/plan/previewRunInfo', { runId, runUid, entityType });
}

/**
 * Fetch plan approvers/approvals.
 * Mirrors: POST /api/textFile/getApprovals { entity: planId, entityType: 'RUN_PLAN_APPROVERS' }
 */
export async function getPlanApprovers(planId: string): Promise<RunApprovers> {
  return post<RunApprovers>('/api/textFile/getApprovals', {
    entity: planId,
    entityType: 'RUN_PLAN_APPROVERS',
  });
}

/**
 * Fetch execute approvers/approvals.
 * Mirrors: POST /api/textFile/getApprovals { entity: executeId, entityType: 'RUN_EXECUTE_APPROVERS' }
 */
export async function getExecuteApprovers(executeId: string): Promise<RunApprovers> {
  return post<RunApprovers>('/api/textFile/getApprovals', {
    entity: executeId,
    entityType: 'RUN_EXECUTE_APPROVERS',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Reports
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch plan reports for a given plan document.
 * Mirrors: GET /api/plan/:planId/reports
 */
export async function getPlanReports(planId: string): Promise<RunReport[]> {
  return get<RunReport[]>(`/api/plan/${planId}/reports`);
}

/**
 * Fetch execute reports for a given run.
 * Mirrors: GET /api/run/:runId/executeReports
 */
export async function getExecuteReports(runId: string): Promise<RunReport[]> {
  return get<RunReport[]>(`/api/run/${runId}/executeReports`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle: Pause / Resume / Abort
// Both call /api/execute/updateStatus then /api/run/updateTimings (same as Angular)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pause a live run.
 * Mirrors: POST /api/execute/updateStatus (pause: true) + POST /api/run/updateTimings (pause: true)
 */
export async function pauseRunFull(
  runId: string,
  runUid: string,
  entityType?: string
): Promise<void> {
  const now = new Date().toISOString();
  await post('/api/execute/updateStatus', {
    runId,
    runUid,
    endTime: now,
    status: 'PAUSED',
    pause: true,
    entityType,
  });
  await post('/api/run/updateTimings', {
    _id: runId,
    uid: runUid,
    endTime: now,
    pause: true,
    status: 'PAUSED',
    entityType,
  });
}

/**
 * Resume a paused run.
 * Mirrors: POST /api/execute/updateStatus (resume: true) + POST /api/run/updateTimings (resume: true)
 */
export async function resumeRunFull(
  runId: string,
  runUid: string,
  entityType?: string
): Promise<void> {
  const now = new Date().toISOString();
  await post('/api/execute/updateStatus', {
    runId,
    runUid,
    startTime: now,
    status: 'LIVE',
    resume: true,
    entityType,
  });
  await post('/api/run/updateTimings', {
    _id: runId,
    uid: runUid,
    startTime: now,
    resume: true,
    status: 'LIVE',
    entityType,
  });
}

/**
 * Update run timings directly (low-level).
 * Mirrors: POST /api/v2/run/updateTimings
 */
export async function updateRunTimings(payload: UpdateTimingsPayload): Promise<RunEntity> {
  return post<RunEntity>('/api/v2/run/updateTimings', {
    ...payload,
    runId: payload._id,
  });
}

/**
 * Update a single execute-step status (low-level).
 * Mirrors: POST /api/execute/updateStatus
 */
export async function updateExecuteStatus(payload: UpdateExecuteStatusPayload): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// Booking returns + cleanup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Return unused booked quantity to inventory after a run step.
 * Mirrors: POST /api/execute/returnBookedQuantity
 */
export async function returnBookedQuantity(
  payload: ReturnBookedQuantityPayload
): Promise<unknown> {
  return post('/api/execute/returnBookedQuantity', payload);
}

/**
 * Mark a material node cleanup as complete.
 * Mirrors: POST /api/execute/cleanup
 */
export async function completeExecuteCleanup(payload: {
  runId: string;
  runUid?: string;
  nodeId: string;
  cloneNumber: number;
}): Promise<ExecuteEntity[]> {
  return post<ExecuteEntity[]>('/api/execute/cleanup', payload);
}

/**
 * Update material destiny in the execute document.
 * Mirrors: POST /api/execute/updateDestiny
 */
export async function updateExecuteDestiny(payload: {
  runId: string;
  nodeId: string;
  cloneNumber: number;
  destiny: unknown;
}): Promise<ExecuteEntity[]> {
  return post<ExecuteEntity[]>('/api/execute/updateDestiny', payload);
}

/**
 * Mark a node as an output node in the execute document.
 * Mirrors: POST /api/execute/mark-output-node
 */
export async function markOutputNode(payload: {
  runId: string;
  nodeId: string;
  cloneNumber: number;
  isOutput?: boolean;
}): Promise<ExecuteEntity[]> {
  return post<ExecuteEntity[]>('/api/execute/mark-output-node', payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// Material / Machine Search + Labs (Mirroring MapMaterials + MapMachines)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Search materials by query string with optional lab filter.
 * Mirrors: POST /api/labs/search/entity { entityType: 'MATERIAL', query, filter }
 */
export async function searchMaterials(
  query: string,
  filter?: string
): Promise<MaterialSearchResult[]> {
  const payload: Record<string, unknown> = { entityType: 'MATERIAL', query };
  if (filter) payload.filter = filter;
  const res = await post<Array<{ entity?: MaterialSearchResult } | MaterialSearchResult>>(
    '/api/labs/search/entity',
    payload
  );
  return res.map((r) => ('entity' in r && r.entity ? r.entity : r) as MaterialSearchResult);
}

/**
 * Search machines/instruments by query string with optional lab filter.
 * Mirrors: POST /api/labs/search/entity { entityType: 'MACHINE', query, filter }
 */
export async function searchMachines(
  query: string,
  filter?: string
): Promise<MachineSearchResult[]> {
  const payload: Record<string, unknown> = { entityType: 'MACHINE', query };
  if (filter) payload.filter = filter;
  const res = await post<Array<{ entity?: MachineSearchResult } | MachineSearchResult>>(
    '/api/labs/search/entity',
    payload
  );
  return res.map((r) => ('entity' in r && r.entity ? r.entity : r) as MachineSearchResult);
}

export interface RecommendedInstrumentOption {
  _id: string;
  value: string;
  label: string;
  name: string;
  uid?: string;
  externalUid?: string;
  workspaceId?: string;
  status?: string;
  instrumentType?: string;
  versionNumber?: string;
  location?: string;
  collection?: string;
}

/**
 * Fetch compact machine data for recommended instrument selection in Add Step modal.
 * Uses dedicated lightweight API: POST /api/machines/get/recommended-instruments
 */
export async function getRecommendedInstrumentOptions(): Promise<RecommendedInstrumentOption[]> {
  const res = await post<Array<{ machineList?: Array<Record<string, unknown>> }>>(
    '/api/machines/get/recommended-instruments',
    {},
  );

  const instrumentList = (Array.isArray(res) ? res : []).flatMap((collection) => {
    const machineList = Array.isArray(collection?.machineList) ? collection.machineList : [];
    return machineList
      .map((instrument) => {
        const id = typeof instrument._id === 'string' ? instrument._id.trim() : '';
        const name = typeof instrument.name === 'string' ? instrument.name.trim() : '';
        if (!id || !name) return null;

        const uid = typeof instrument.uid === 'string' ? instrument.uid.trim() : undefined;
        const externalUid = typeof instrument.externalUid === 'string' ? instrument.externalUid.trim() : undefined;
        const workspaceId =
          (typeof instrument.workspaceId === 'string' && instrument.workspaceId.trim()) ||
          (typeof instrument.orgId === 'string' && instrument.orgId.trim()) ||
          undefined;

        const rawLocation = instrument.location;
        const location = typeof rawLocation === 'string'
          ? rawLocation.trim()
          : rawLocation && typeof rawLocation === 'object' && !Array.isArray(rawLocation)
            ? (() => {
                const locationObj = rawLocation as Record<string, unknown>;
                const nested = locationObj.location;
                const locationName = nested && typeof nested === 'object' && !Array.isArray(nested)
                  ? (typeof (nested as Record<string, unknown>).name === 'string'
                      ? String((nested as Record<string, unknown>).name).trim()
                      : '')
                  : typeof nested === 'string'
                    ? nested.trim()
                    : '';
                const subPosition = typeof locationObj.subPosition === 'string'
                  ? locationObj.subPosition.trim()
                  : '';
                return locationName && subPosition
                  ? `${locationName} - ${subPosition}`
                  : locationName || subPosition || undefined;
              })()
            : undefined;

        const collectionName = typeof instrument.collection === 'string'
          ? instrument.collection.trim()
          : '';

        return {
          _id: id,
          value: id,
          label: uid ? `${name} - ${uid}` : name,
          name,
          uid,
          externalUid,
          workspaceId,
          status: typeof instrument.status === 'string' ? instrument.status.trim() : undefined,
          instrumentType: typeof instrument.instrumentType === 'string' ? instrument.instrumentType.trim() : undefined,
          versionNumber: instrument.versionNumber != null ? String(instrument.versionNumber) : undefined,
          location,
          collection: collectionName || undefined,
        } as RecommendedInstrumentOption;
      })
      .filter((item): item is RecommendedInstrumentOption => Boolean(item));
  });

  const uniqueById = new Map<string, RecommendedInstrumentOption>();
  instrumentList.forEach((item) => {
    if (!uniqueById.has(item.value)) {
      uniqueById.set(item.value, item);
    }
  });

  return Array.from(uniqueById.values());
}

/**
 * Get all materials in a given lab.
 * Mirrors: POST /api/materials/get/lab { labId, sample: true }
 */
export async function getMaterialsByLab(labId: string): Promise<MaterialSearchResult[]> {
  return post<MaterialSearchResult[]>('/api/materials/get/lab', { labId, sample: true });
}

/**
 * Fetch user's lab collections for filter dropdown.
 * Mirrors: POST /api/users/get/labs
 */
export async function getUserLabs(): Promise<LabCollection[]> {
  return post<LabCollection[]>('/api/users/get/labs', {});
}

/**
 * Get current user data.
 * Mirrors: GET /api/users/me
 */
export async function getCurrentUser(): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>('/api/users/me');
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan Destiny
// Mirrors: POST /api/plan/updateDestiny
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Update material destiny in plan.
 * Mirrors: POST /api/plan/updateDestiny { destiny, node, runId, cloneNumber, runUid }
 */
export async function updatePlanDestiny(payload: {
  runId: string;
  runUid?: string;
  node: string;
  cloneNumber: number;
  destiny: unknown;
}): Promise<PlanEntity> {
  return post<PlanEntity>('/api/plan/updateDestiny', payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan Booking
// Mirrors: POST /api/materials/bookings/get, /api/plan/update, /api/plan/deleteBooking
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check existing bookings for a material entity before booking.
 * Mirrors: POST /api/materials/bookings/get
 */
export async function getMaterialBookings(payload: {
  entityId: string;
  forConsolidation?: boolean;
  bookingStatus?: string;
  usageStatus?: string;
}): Promise<unknown[]> {
  return post<unknown[]>('/api/materials/bookings/get', payload);
}

/**
 * Delete a material booking from plan.
 * Mirrors: POST /api/plan/deleteBooking
 */
export async function deletePlanBooking(payload: {
  nodeId: string;
  cloneNumber: number;
  runId: string;
  runUid?: string;
}): Promise<PlanEntity> {
  return post<PlanEntity>('/api/plan/deleteBooking', payload);
}

/**
 * Cancel a booking reference.
 * Mirrors: PUT /api/materials/bookings/{id}/cancel
 */
export async function cancelBookingReference(
  bookingId: string
): Promise<unknown> {
  const res = await fetch(`${ILA_API_BASE}/api/materials/bookings/${encodeURIComponent(bookingId)}/cancel`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify({ isRun: true }),
  });
  return handleResponse(res, `/api/materials/bookings/${bookingId}/cancel`);
}

/**
 * Replace material names in execute steps after booking change.
 * Mirrors: POST /api/execute/replaceName
 */
export async function replaceExecuteName(payload: {
  runId: string;
  runUid?: string;
  bookedMaterials?: unknown[];
  deleteBooking?: boolean;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/v2/execute/replaceName', payload);
}

// ─────────────────────────────────────────────────────────────────────────────
// Machine Booking
// Mirrors: POST /api/execute/deleteBooking (for machine deletion)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Delete a machine booking from execute.
 * Mirrors: POST /api/execute/deleteBooking
 */
export async function deleteExecuteBooking(payload: {
  stepId: string;
  cloneNumber: number;
  operationCloneNumber?: number;
  runId: string;
  runUid?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/deleteBooking', payload);
}

/**
 * Get plan graph (edges + nodes) for run.
 * Mirrors: POST /api/v2/plan/getGraph
 */
export async function getPlanGraphEdges(
  runId: string,
  runUid?: string
): Promise<{ graph?: { edges?: PlanEdge[]; vertices?: unknown[] } }> {
  return post('/api/v2/plan/getGraph', { runId, runUid });
}

/**
 * Update plan (material booking, machine booking, time selection).
 * Mirrors: POST /api/plan/update
 */
export async function updatePlan(payload: {
  runId: string;
  runUid?: string;
  timeChosen?: string | null;
  nodes?: unknown[];
  steps?: unknown[];
  operationSteps?: unknown[];
  [key: string]: unknown;
}): Promise<PlanEntity> {
  return post<PlanEntity>('/api/plan/update', payload);
}

/**
 * Update execute status (machine booking sync).
 * Mirrors: POST /api/execute/updateStatus
 */
export async function updateExecuteStatusForBooking(payload: {
  runId: string;
  runUid?: string;
  steps?: unknown[];
  runExecuteSteps?: unknown[];
  [key: string]: unknown;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', payload);
}

/**
 * Get version runs for "Select Run Bookings" dropdown.
 * Mirrors: POST /api/v2/run/list
 */
export async function getVersionRuns(versionId: string): Promise<unknown[]> {
  return post<unknown[]>('/api/v2/run/list', { versionId });
}

/**
 * Automate material bookings from another run.
 * Mirrors: POST /api/bookings/automate
 */
export async function automateBookings(sourceRun: string, targetRun: string): Promise<unknown> {
  return post('/api/bookings/automate', { sourceRun, targetRun });
}

/**
 * Reset run (clear all bookings).
 * Mirrors: POST /api/v2/run/reset
 */
export async function resetRun(runId: string): Promise<unknown> {
  return post('/api/v2/run/reset', { runId });
}

/**
 * Get preview plan info (enriched step translations).
 * Mirrors: POST /api/plan/previewRunInfo
 */
export async function getPreviewPlan(
  runId: string,
  runUid?: string
): Promise<PlanPreviewData> {
  return post<PlanPreviewData>('/api/plan/previewRunInfo', { runId, runUid });
}

// ─────────────────────────────────────────────────────────────────────────────
// Consolidated resolver-style bootstrap
// Equivalent to what RunResolver calls on /dashboard route
// ─────────────────────────────────────────────────────────────────────────────

export interface RunDashboardBootstrap {
  runInfo: RunInfoResponse;
  plan: PlanEntity | null;
  execute: ExecuteEntity | null;
  uiElements: RunUIPermissions;
  planGraph: PlanGraph;
}

/**
 * Load all data needed to render the run dashboard in one call.
 * Mirrors the parallel fetches inside RunResolver for the /dashboard route.
 * Non-critical fetches (planGraph, uiElements) resolve to safe defaults on error.
 */
export async function bootstrapRunDashboard(
  runId: string,
  entityType?: string,
  source?: string
): Promise<RunDashboardBootstrap> {
  const [runInfo, plan, execute, uiElements, planGraph] = await Promise.all([
    getRunInfo(runId, source),
    getRunPlan(runId, entityType, source).catch(() => null),
    getRunExecute(runId, entityType, source).catch(() => null),
    getRunUIElements(runId, entityType, source).catch(() => ({
      canPause: false,
      canResume: false,
      canAbort: false,
      canOnlineMode: false,
      canOfflineMode: false,
    })),
    getPlanGraph(runId, source).catch(() => ({})),
  ]);
  return { runInfo, plan, execute, uiElements, planGraph };
}

// ─────────────────────────────────────────────────────────────────────────────
// Execute Step Runner APIs
// New APIs for the redesigned Execute Step Runner UI.
// These do NOT overwrite existing ones — they are new wrappers for execute operations.
// ─────────────────────────────────────────────────────────────────────────────

// ─── v2 Execute APIs (strategy-based, executeGraph-backed) ───────────────────

export async function executeInitV2(payload: {
  runId: string;
}): Promise<{ success: boolean; data: ExecuteEntity }> {
  return post('/api/v2/execute/init', payload);
}

export async function executeGetV2(payload: {
  runId: string;
}): Promise<{ success: boolean; data: { execute: ExecuteEntity; steps: unknown[]; parameterDiffs: unknown[] } }> {
  return post('/api/v2/execute/get', payload);
}

export async function executeCompleteStepV2(payload: {
  runId: string;
  stepId: string;
  actualQtyUsed?: { qtyType?: string; units?: string; amount?: number };
  endTime?: string;
}): Promise<{ success: boolean; data: { step: { stepId: string; status: string }; quantityUpdates: unknown[]; nextStepId: string | null } }> {
  return post('/api/v2/execute/completeStep', payload);
}

export async function executeSkipStepV2(payload: {
  runId: string;
  stepId: string;
  reason?: string;
}): Promise<{ success: boolean; data: { step: { stepId: string; status: string }; nextStepId: string | null } }> {
  return post('/api/v2/execute/skipStep', payload);
}

export async function executeRepeatStepV2(payload: {
  runId: string;
  stepId: string;
  cloneNumber?: number;
}): Promise<{ success: boolean; data: { originalStep: unknown; repeatedStep: unknown; createdVertices: unknown[]; createdEdges: unknown[] } }> {
  return post('/api/v2/execute/repeatStep', payload);
}

export async function executeApproveStepV2(payload: {
  runId: string;
  stepId: string;
  decision: string;
  comment?: string;
}): Promise<{ success: boolean; data: { step: { stepId: string }; approval: unknown } }> {
  return post('/api/v2/execute/approveStep', payload);
}

// ─── Legacy Execute APIs ─────────────────────────────────────────────────────

/**
 * Start an execute step (sets status to IN_PROGRESS with startTime).
 * Mirrors: POST /api/execute/updateStatus
 */
export async function executeStartStep(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
  startTime?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    ...payload,
    status: 'IN_PROGRESS',
    startTime: payload.startTime ?? new Date().toISOString(),
  });
}

/**
 * Complete an execute step (sets completed=true, status=COMPLETED, endTime).
 * Validates checkpoints before allowing completion on the frontend.
 * Mirrors: POST /api/execute/updateStatus
 */
export async function executeCompleteStep(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
  endTime?: string;
  actualQtyUsed?: { qtyType?: string; units?: string; amount?: number };
  context?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    ...payload,
    completed: true,
    status: 'COMPLETED',
    endTime: payload.endTime ?? new Date().toISOString(),
  });
}

/**
 * Skip an execute step (sets status=SKIPPED, completed=true).
 * Mirrors: POST /api/execute/updateStatus
 */
export async function executeSkipStep(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    ...payload,
    status: 'SKIPPED',
    completed: true,
  });
}

/**
 * Repeat an execute step (creates a new step instance after the current one).
 * Mirrors: POST /api/run/repeatStep (runHelper.createRepeatStep)
 */
export async function executeRepeatStep(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
}): Promise<unknown> {
  return post('/api/run/repeatStep', payload);
}

/**
 * Finish the execute run (mark execute as completed).
 * Mirrors: POST /api/execute/updateStatus with executeCompleted flag
 */
export async function executeFinishRun(payload: {
  runId: string;
  runUid?: string;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    ...payload,
    executeCompleted: true,
  });
}

/**
 * Update edge properties for a step (e.g., checkpoint responses, usage instructions).
 * Context: 'CHECKPOINT_UPDATED' avoids marking the edge as changed.
 * Mirrors: POST /api/execute/updateEdgeProperties
 */
export async function executeUpdateEdgeProperties(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  /** MongoDB _id of the edge document (required by backend findOne) */
  edgeDocId?: string;
  cloneNumber: number;
  properties?: Record<string, unknown>;
  context?: string;
}): Promise<unknown> {
  const { stepId, edgeDocId, ...rest } = payload;
  return post('/api/execute/updateEdgeProperties', {
    ...rest,
    // Backend expects `edgeId` (the logical edge identifier) and `_id` (MongoDB doc id)
    edgeId: stepId,
    _id: edgeDocId,
  });
}

/**
 * Update parameters on an OperationVertex (operationsInNodes / v2 maxGraph runs).
 * Mirrors: POST /api/execute/updateVertexParameters
 */
export async function executeUpdateVertexParameters(payload: {
  runId: string;
  runUid?: string;
  vertexId: string;
  cloneNumber: number;
  parameters: Record<string, unknown>;
  context?: string;
}): Promise<unknown> {
  return post('/api/execute/updateVertexParameters', payload);
}

/**
 * Update usage instructions (machine checklists) for a step.
 * Mirrors: POST /api/execute/updateStatus with usageInstructions field
 */
export async function executeUpdateUsageInstructions(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
  machineIndex: number;
  usageInstructions: unknown;
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    runId: payload.runId,
    runUid: payload.runUid,
    stepId: payload.stepId,
    cloneNumber: payload.cloneNumber,
    usageInstructions: payload.usageInstructions,
  });
}

/**
 * Upload a file to an execute step.
 * Mirrors: POST /api/execute/updateStatus with files field
 */
export async function executeUploadStepFile(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
  files: string[];
}): Promise<ExecuteEntity> {
  return post<ExecuteEntity>('/api/execute/updateStatus', {
    runId: payload.runId,
    runUid: payload.runUid,
    stepId: payload.stepId,
    cloneNumber: payload.cloneNumber,
    files: payload.files,
  });
}

/**
 * Delete a step file attachment.
 * Mirrors: POST /api/execute/deleteStepFile
 */
export async function executeDeleteStepFile(payload: {
  runId: string;
  runUid?: string;
  stepId: string;
  cloneNumber: number;
  fileId: string;
}): Promise<unknown> {
  return post('/api/execute/deleteStepFile', payload);
}

export interface ExecuteTranslationEntry {
  stepId: string;
  cloneNumber: number;
  translation: string;
  order?: number;
  creationOrder?: number;
  executeOrder?: number;
  orderString?: string;
  actionType?: string;
  /** edge.properties.value — contains parameters, hideParamsMap, etc. */
  properties?: {
    parameters?: Record<string, unknown>;
    hideParamsMap?: Record<string, boolean | number>;
    [key: string]: unknown;
  };
  comments?: string;
}

export interface ExecuteTranslationResult {
  metaData: unknown;
  translations: ExecuteTranslationEntry[];
}

/**
 * Get all execute-stage translations for one or more runs.
 * Mirrors: POST /api/execute/getTranslation  { runs: [runId, ...] }
 */
export async function executeGetTranslation(payload: {
  runs: string[];
}): Promise<ExecuteTranslationResult[]> {
  return post('/api/execute/getTranslation', payload);
}

/**
 * Fetch fresh translations after parameters change.
 * Mirrors: POST /api/execute/executeTranslation  { runs: [runId, ...] }
 */
export async function executeRunTranslation(payload: {
  runs: string[];
}): Promise<ExecuteTranslationResult[]> {
  return post('/api/execute/executeTranslation', payload);
}

/**
 * Mark a run as fully completed.
 * Mirrors: POST /api/v2/run/markAsCompleted
 */
export async function markRunAsCompleted(payload: {
  runId: string;
  runUid?: string;
}): Promise<unknown> {
  return post('/api/v2/run/markAsCompleted', payload);
}

/**
 * Clone plan graph for execute.
 * Mirrors: POST /api/run/clonePlanGraph
 */
// export async function clonePlanGraph(payload: {
//   runId: string;
//   runUid?: string;
//   entityType?: string;
// }): Promise<unknown> {
//   return post('/api/run/clonePlanGraph', payload);
/**
 * Clone plan graph for execute (creates run.executeGraph from run.graph).
 * Mirrors: POST /api/v2/run/clonePlanGraph
 */
export async function clonePlanGraph(payload: {
  runId: string;
  runUid?: string;
  entityType?: string;
}): Promise<unknown> {
  return post('/api/v2/run/clonePlanGraph', payload);
}

